import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, CircularProgress } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useSnackbar } from 'notistack';

import { routes } from '@app/routes';
import { useGetAssessmentById, useSubmitAssessment } from '@features/assessment/api';
import { useGetDocumentDownloadUrl } from '@features/assessment/api/documents';
import {
  CompletionBanner,
  ConfirmSubmissionModal,
  DomainSummaryList,
  StatsCards,
  SummaryHeader,
  WarningBanner,
} from '@features/assessment/components/summary';
import { ReassessmentBanner } from '@features/assessment/components/workflow/ReassessmentBanner';
import { AssessmentDomainStatus, AssessmentStatus } from '@libs/constants';
import { Trans, useTrans } from '@libs/i18n';
import { PreviousDomainSummary } from '@libs/types';
import { hasValidClassification } from '@libs/utils';

export const AssessmentSummaryPage: FC = observer(function AssessmentSummaryPage() {
  const { assessmentId } = useParams<{ assessmentId: string }>();

  // Early return if assessmentId is missing.
  //
  // Kept in this thin wrapper, above a component boundary, so the body below
  // never has hooks sitting under an early return. Bailing out here changes
  // which component type renders (React unmounts the body); bailing out inside
  // the body would change the hook count of a component that stays mounted,
  // which is React error #310.
  if (!assessmentId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          <Trans i18nKey="app.assessments.error.invalidAssessmentId" />
        </Alert>
      </Box>
    );
  }

  return <AssessmentSummaryPageContent assessmentId={assessmentId} />;
});

/**
 * The page body. All hooks run unconditionally; the loading/error early returns
 * further down have no hook after them.
 */
const AssessmentSummaryPageContent: FC<{ assessmentId: string }> = observer(function AssessmentSummaryPageContent({
  assessmentId,
}: { assessmentId: string }) {
  const { t } = useTrans();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);

  // Fetch assessment data with all domains and parent-version summaries.
  // The two `includePrevious*` flags are no-ops on v1 assessments — the API
  // simply omits the corresponding fields — so passing them unconditionally
  // is safe and keeps the request shape simple.
  const {
    data: assessment,
    isLoading,
    isError,
    error,
  } = useGetAssessmentById(
    { assessmentId },
    {
      includeDomains: true,
      includePreviousAssessmentSummary: true,
      includePreviousDomainsSummary: true,
    },
    { refetchOnMount: 'always' },
  );

  // Mutations
  const submitAssessmentMutation = useSubmitAssessment();
  const downloadDocumentMutation = useGetDocumentDownloadUrl();

  // Match parent domains to current domains by frameworkDomain orderIndex —
  // the only stable identity across versions (assessmentDomain ids are
  // recreated each version while orderIndex tracks the framework domain).
  // Memoised here, ABOVE the loading/error early returns, so the hook is
  // called in the same order on every render. `assessment` may be undefined
  // on the first render — falls back to an empty map.
  const previousByOrderIndex = useMemo(() => {
    const map = new Map<number, PreviousDomainSummary>();
    (assessment?.previousDomainsSummary ?? []).forEach((pd) => {
      map.set(pd.assessmentDomain.orderIndex, pd);
    });
    return map;
  }, [assessment?.previousDomainsSummary]);

  // Guard: Only allow draft assessments with all domains completed
  useEffect(() => {
    if (!assessment || isLoading) return;

    const isDraft = assessment.status === AssessmentStatus.DRAFT;
    const allDomainsCompleted =
      assessment.assessmentDomains &&
      assessment.assessmentDomains.length > 0 &&
      assessment.assessmentDomains.every(
        (domain) => domain.status === AssessmentDomainStatus.COMPLETED
      );

    if (!isDraft || !allDomainsCompleted) {
      // Show appropriate error message
      if (!isDraft) {
        enqueueSnackbar(t('app.assessments.error.notDraftStatus'), {
          variant: 'warning',
        });
      } else if (!allDomainsCompleted) {
        enqueueSnackbar(t('app.assessments.error.domainsNotCompleted'), {
          variant: 'warning',
        });
      }

      navigate(routes.assessmentList(), { replace: true });
    }
  }, [assessment, isLoading, navigate, enqueueSnackbar]);

  // Handlers
  const handleSubmitClick = () => {
    setIsSubmitModalOpen(true);
  };

  const handleSubmitConfirm = () => {
    if (assessmentId) {
      submitAssessmentMutation.mutate({ assessmentId });
      setIsSubmitModalOpen(false);
    }
  };

  const handleSubmitCancel = () => {
    setIsSubmitModalOpen(false);
  };

  const handleDocumentDownload = (documentId: string) => {
    downloadDocumentMutation.mutate({ documentId });
  };

  // Loading state
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (isError || !assessment) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {error?.message || t('app.assessments.error.failedToLoad')}
        </Alert>
      </Box>
    );
  }

  // Check guard conditions to prevent content flash
  const isDraft = assessment.status === AssessmentStatus.DRAFT;
  const allDomainsCompleted =
    assessment.assessmentDomains &&
    assessment.assessmentDomains.length > 0 &&
    assessment.assessmentDomains.every(
      (domain) => domain.status === AssessmentDomainStatus.COMPLETED
    );

  // Show loading while redirecting (prevent flash)
  if (!isDraft || !allDomainsCompleted) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Calculate stats
  const domains = (assessment.assessmentDomains || []).sort((a, b) => (a.frameworkDomain?.orderIndex ?? 0) - (b.frameworkDomain?.orderIndex ?? 0));
  const totalDomains = domains.length;
  const completedDomains = domains.filter((d) => d.status === 'completed').length;
  const mandatoryDomains = domains.filter((d) => d.frameworkDomain?.required === true).length;
  const optionalDomains = domains.filter((d) => d.frameworkDomain?.required === false).length;
  const totalDocuments = domains.reduce((acc, domain) => {
    return acc + (domain.documents?.length || 0);
  }, 0);
  // Not-applicable domains are excluded from missing-document accounting —
  // their standard documents are irrelevant because the org opted out of the
  // domain entirely. Skipped (but applicable) domains still contribute, since
  // the user agreed the domain applies but uploaded no evidence.
  const totalStandardDocuments = domains.reduce((acc, domain) => {
    if (!domain.isApplicable) return acc;
    return acc + (domain.frameworkDomain?.standardDocuments?.length ?? 0);
  }, 0);
  const validlyClassifiedDocuments = domains.reduce((acc, domain) => {
    if (!domain.isApplicable) return acc;
    const count = (domain.documents ?? []).filter(
      (doc) => !doc.deleted && hasValidClassification(doc),
    ).length;
    return acc + count;
  }, 0);
  const totalMissingDocuments = Math.max(0, totalStandardDocuments - validlyClassifiedDocuments);

  // ============================================================================
  // RE-ASSESSMENT DERIVATIONS (only meaningful when version > 1 with a parent)
  // ============================================================================
  // `previousByOrderIndex` is memoised above the early-return guards so the
  // hook order stays stable across renders.
  const previousAssessmentSummary = assessment.previousAssessmentSummary;
  const isReassessment = !!previousAssessmentSummary;

  const editedDomainCount = isReassessment
    ? domains.filter((d) => d.isModifiedFromPrevious).length
    : 0;
  const parentVersion = previousAssessmentSummary?.assessment.version;
  const parentSubmittedAt = previousAssessmentSummary?.assessment.submittedAt ?? null;
  const parentTotalDocuments = previousAssessmentSummary?.results.documentsAssessed;
  const parentTotalMissing = previousAssessmentSummary?.results.missingDocuments;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        height: '100vh',
        backgroundColor: 'background.default',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
      }}
    >
      {/* Header */}
      <SummaryHeader
        assessmentTitle={assessment.title || t('app.assessments.untitled')}
        onSubmitClick={handleSubmitClick}
        assessmentLanguage={assessment.language as { code: string; label: string } | undefined}
      />
      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          py: 3,
        }}
      >

        <Box
          sx={{
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          {/* Re-assessment context banner — only when version > 1 with a parent */}
          {isReassessment && parentVersion != null && (
            <ReassessmentBanner
              currentVersion={assessment.version}
              parentVersion={parentVersion}
              parentSubmittedAt={parentSubmittedAt}
            />
          )}

          {/* Completion Banner */}
          <CompletionBanner
            domainCount={totalDomains}
            documentCount={totalDocuments}
          />

          {/* Stats Cards */}
          <StatsCards
            totalDomains={totalDomains}
            completedDomains={completedDomains}
            mandatoryDomains={mandatoryDomains}
            optionalDomains={optionalDomains}
            totalDocuments={totalDocuments}
            totalMissingDocuments={totalMissingDocuments}
            editedDomainCount={isReassessment ? editedDomainCount : undefined}
            parentTotalDocuments={parentTotalDocuments}
            parentTotalMissing={parentTotalMissing}
            parentVersion={parentVersion}
          />

          {/* Warning Banner */}
          <WarningBanner />

          {/* Domain Summary List */}
          <DomainSummaryList
            assessmentId={assessmentId}
            domains={domains}
            previousByOrderIndex={previousByOrderIndex}
            parentVersion={parentVersion}
            onDownloadDocument={handleDocumentDownload}
            onSubmitClick={handleSubmitClick}
          />
        </Box>
      </Box>

      {/* Confirmation Modal */}
      <ConfirmSubmissionModal
        open={isSubmitModalOpen}
        onClose={handleSubmitCancel}
        onConfirm={handleSubmitConfirm}
        isSubmitting={submitAssessmentMutation.isPending}
      />
    </Box>
  );
});
